import { apiSlice } from "api/api.slice";
import type {
  Dimension,
  GetDimensionByIdResponse,
  GetDimensionsResponse,
  IdArg,
  SearchDimensionsArg,
} from "api/api.types";
import { QUERY_LIMIT } from "api/constants";
import {
  dimensionSchema,
  getDimensionByIdResponseSchema,
  getDimensionsResponseSchema,
} from "api/schemas/dimensionApiSchema";

export type GetDimensionsArg = { limit?: number; offset: number; page?: "search"; search?: string };

export const dimensionsApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getDimension: build.query<GetDimensionByIdResponse, IdArg>({
      query: ({ id }) => ({
        headers: { "x-api-version": "v2" },
        url: `/dimension/${id}`,
        schema: getDimensionByIdResponseSchema,
      }),
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- allowing void as a type here so that when you use the hook you do not need to pass an argument
    getDimensions: build.infiniteQuery<GetDimensionsResponse, void, number>({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.dimensions.length < QUERY_LIMIT) return undefined;

          return lastPageParam + 1;
        },
      },
      query: ({ pageParam }) => {
        const offset = (pageParam - 1) * QUERY_LIMIT;

        return {
          headers: { "x-api-version": "v2" },
          url: "/dimension",
          params: {
            has_parent: "true",
            limit: QUERY_LIMIT,
            offset,
            statuses: "Active",
          },
          schema: getDimensionsResponseSchema,
        };
      },
    }),
    searchDimensions: build.infiniteQuery<Dimension[], SearchDimensionsArg, number>({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.length < QUERY_LIMIT) return undefined;

          return lastPageParam + 1;
        },
      },
      query: ({ queryArg, pageParam }) => {
        const offset = (pageParam - 1) * QUERY_LIMIT;

        return {
          headers: { "x-api-version": "v2" },
          url: "/dimension/similar/text",
          params: {
            has_parent: "true",
            limit: QUERY_LIMIT,
            offset,
            statuses: "Active",
          },
          // POST is used because the body argument `text` is used to create a vector embedding, which as a result will be used to search for similar results
          method: "POST",
          body: { data: { text: queryArg.search }, similarity_mode: "hybrid" },
          schema: dimensionSchema.array(),
        };
      },
    }),
  }),
});

export const { useGetDimensionQuery, useLazyGetDimensionQuery } = dimensionsApiSlice;
